<script setup lang="ts">
import useWordOfTheDayStore from '../store';
import { storeToRefs } from 'pinia';
import Flower from './Flower.vue';
import { useDisplay } from 'vuetify';
import { computed, ref, watch } from 'vue';
import useKeywordSearch from '../composables/use-keyword-search';
import type { KeywordSearchResult } from '@ilteducation/word-of-the-day-types';

const store = useWordOfTheDayStore();
const { keywordOfTheDay, wordSource } = storeToRefs(store);

const { query, results, isLoading, setQuery } = useKeywordSearch();

// While a search is running we hide results and show a spinner instead.
const displayItems = computed(() => (isLoading.value ? [] : results.value));
// Only show "no results" once a search has actually completed for a non-empty query.
const showNoResults = computed(() => !isLoading.value && query.value.trim().length > 0);

// 'global' = today's word, 'custom' = the user picks their own word.
const mode = ref<'global' | 'custom'>(wordSource.value);
const selected = ref<KeywordSearchResult | null>(null);

// Drive the dropdown manually so it only opens once there is something to show
// (a spinner, results, or a "no results" message) — never an empty box on focus.
const menuOpen = ref(false);
const hasMenuContent = computed(() => isLoading.value || displayItems.value.length > 0 || showNoResults.value);
// Open as soon as there's something to show, close when there's nothing.
watch(hasMenuContent, (has) => {
  menuOpen.value = has;
});
// Block Vuetify from opening an empty menu when the field is focused/clicked.
watch(menuOpen, (open) => {
  if (open && !hasMenuContent.value) {
    menuOpen.value = false;
  }
});

watch(mode, (next) => {
  if (next === 'global') {
    selected.value = null;
    store.useTodaysGlobalWord();
  }
});

const onSearch = (value: string) => {
  // On selection Vuetify re-emits the picked item's title as the search term; don't re-search for it.
  if (selected.value && value === selected.value.keyword) {
    return;
  }
  setQuery(value);
};

const onSelect = (result: KeywordSearchResult | null) => {
  if (result) {
    store.selectCustomWord(result);
  }
  // Reset the search and close the dropdown after a pick (or clear).
  setQuery('');
  menuOpen.value = false;
};

const canStart = computed(() => !!keywordOfTheDay.value && (mode.value === 'global' || wordSource.value === 'custom'));

const sendGameStartEvent = () => {
  store.sendGameStartEvent();
};

const start = () => {
  sendGameStartEvent();
  store.setGameState('game');
};

const { lgAndUp } = useDisplay();
</script>

<template>
  <div class="the-game">
    <h1 class="title">Dagens ord</h1>
    <Flower class="init-flower" />

    <v-btn-toggle v-model="mode" mandatory variant="outlined" divided base-color="on-surface" class="mode-toggle mb-6">
      <v-btn value="global">Dagens ord</v-btn>
      <v-btn value="custom">Välj eget ord</v-btn>
    </v-btn-toggle>

    <v-autocomplete
      v-if="mode === 'custom'"
      v-model="selected"
      v-model:menu="menuOpen"
      :items="displayItems"
      :loading="isLoading"
      item-title="keyword"
      return-object
      no-filter
      clearable
      hide-details
      variant="outlined"
      density="comfortable"
      placeholder="Sök efter ett begrepp"
      class="word-search mb-6"
      @update:search="onSearch"
      @update:model-value="onSelect"
    >
      <template #no-data>
        <div v-if="isLoading" class="px-4 py-3 d-flex align-center justify-center">
          <v-progress-circular indeterminate size="24" width="3" />
        </div>
        <div v-else-if="showNoResults" class="px-4 py-3 text-medium-emphasis">Inga ord hittades</div>
      </template>
    </v-autocomplete>

    <v-btn
      v-if="keywordOfTheDay"
      variant="outlined"
      color="on-surface"
      @click="start"
      :disabled="!canStart"
      prepend-icon="mdi-flag-checkered"
      class="action-btn"
      :height="lgAndUp ? '56px' : '48px'"
      >Börja spela
    </v-btn>
    <v-progress-circular v-else indeterminate size="64"></v-progress-circular>
  </div>
</template>

<style scoped lang="scss">
.the-game {
  display: flex;
  height: 100%;
  min-height: 0;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: clamp(1rem, 3vh, 2.5rem);
  padding: clamp(1rem, 3vh, 2.5rem) 1rem;
}

// Shrinks first when the viewport is short so the toggle + start button stay visible.
:deep(.init-flower) {
  flex: 1 1 0;
  min-height: 0;
  max-height: 45vh;
}

.title {
  transform-origin: top left;
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: bold;
  font-family: 'Chalkduster', sans-serif;
  flex: 0 0 auto;
}

// Mirrors the toggle styling in quizter/views/host/SettingsScreenView.vue:
// transparent fill, faint white border when unselected, brighter full-white outline + text when selected.
:deep(.mode-toggle) {
  .v-btn--active {
    background-color: transparent !important;
    border: 2px solid rgb(var(--v-theme-on-surface)) !important;
    color: rgb(var(--v-theme-on-surface)) !important;

    .v-btn__overlay {
      opacity: 0 !important;
    }

    .v-btn__underlay {
      display: none;
    }
  }

  .v-btn {
    border: 1px solid rgba(var(--v-theme-on-surface), 0.5);
    color: rgba(var(--v-theme-on-surface));

    &:hover {
      border-color: rgb(var(--v-theme-on-surface));
    }
  }
}

.word-search {
  width: min(420px, 80vw);
  max-height: 48px;
}
</style>
